import React from "react";
import { Link, useLocation, Outlet } from "react-router-dom";
import { 
  Squares2X2Icon,
  MagnifyingGlassIcon,
  DocumentArrowUpIcon,
  ListBulletIcon,
  CodeBracketSquareIcon,
  ShieldCheckIcon,
  Cog6ToothIcon, 
  QuestionMarkCircleIcon,
  BellIcon,
  ClockIcon
} from "@heroicons/react/24/outline";
import { RocketLaunchIcon } from "@heroicons/react/24/solid";
import { useAuthStore } from "../../../store/useAuthStore";
import { useLogout } from "../../../features/auth/api/auth";

const navItems = [
  { name: "Dashboard", path: "/", icon: Squares2X2Icon },
  { name: "Search", path: "/search", icon: MagnifyingGlassIcon },
  { name: "Bulk Upload", path: "/bulk-upload", icon: DocumentArrowUpIcon },
  { name: "My Lists", path: "/lists", icon: ListBulletIcon },
  { name: "API Access", path: "/api-access", icon: CodeBracketSquareIcon },
];

export default function DashboardLayout() {
  const location = useLocation();
  const { user } = useAuthStore();
  const { mutate: logout } = useLogout();

  return (
    <div className="flex h-screen bg-surface-50 font-sans overflow-hidden">
      {/* Sidebar */}
      <aside className="w-[280px] bg-white border-r border-slate-200 flex flex-col justify-between hidden md:flex shrink-0">
        <div className="flex flex-col h-full">
          {/* Logo Area */}
          <div className="h-24 flex items-center px-8 shrink-0">
            <div className="flex items-center gap-3.5">
              <div className="w-10 h-10 bg-brand-500 rounded-full flex items-center justify-center shadow-md shadow-brand-500/20 shrink-0">
                <RocketLaunchIcon className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-[20px] font-extrabold text-slate-900 tracking-tight">Founder Finder</h1>
            </div>
          </div>
          
          {/* Navigation Links */}
          <nav className="px-5 space-y-1.5 flex-1 overflow-y-auto pt-2">
            {navItems.map((item) => {
              // FIXED: Only highlight if the path matches exactly
              const isActive = location.pathname === item.path; 
              
              return (
                <Link 
                  key={item.name} 
                  to={item.path} 
                  className={`flex items-center gap-3.5 px-4 py-3 rounded-xl text-[15px] font-semibold transition-all duration-200 ${
                    isActive 
                      ? "bg-brand-500 text-white shadow-md shadow-brand-500/20" 
                      : "text-slate-500 hover:bg-slate-50 hover:text-slate-900"
                  }`}
                >
                  <item.icon className={`w-[22px] h-[22px] shrink-0 ${isActive ? 'text-white' : 'text-slate-400'}`} />
                  {item.name}
                </Link>
              );
            })}
          </nav>
        </div>

        {/* Bottom Section */}
        <div className="p-6 flex flex-col gap-1.5 shrink-0">
          {/* Premium Upgrade Card */}
          <div className="bg-[#0f172a] rounded-2xl p-5 mb-3 shadow-xl shadow-slate-900/10">
            <h3 className="text-white text-[14px] font-bold">Get Premium Plan</h3>
            <p className="text-slate-400 text-[12px] leading-relaxed mt-1.5 mb-4 pr-2">
              Get unlimited data extraction and premium insights.
            </p>
            <button className="w-full bg-white text-slate-900 py-2.5 rounded-xl text-sm font-bold hover:bg-slate-100 hover:scale-[1.02] active:scale-95 transition-all">
              Upgrade Now
            </button>
          </div>

          <button className="w-full flex items-center gap-3.5 px-3 py-2.5 text-[15px] font-semibold text-slate-500 hover:bg-slate-50 hover:text-slate-900 rounded-xl transition-colors">
            <QuestionMarkCircleIcon className="w-6 h-6 text-slate-400 shrink-0" /> Support
          </button>
          <button className="w-full flex items-center gap-3.5 px-3 py-2.5 text-[15px] font-semibold text-slate-500 hover:bg-slate-50 hover:text-slate-900 rounded-xl transition-colors">
            <Cog6ToothIcon className="w-6 h-6 text-slate-400 shrink-0" /> Settings
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col h-screen overflow-hidden">
        {/* Topbar matching image_f38e83.png */}
        <header className="h-[88px] bg-white border-b border-slate-200 flex items-center justify-between px-10 shrink-0 relative z-20">
          
          {/* Wide Search Bar */}
          <div className="flex-1 max-w-3xl">
            <div className="relative group">
              <MagnifyingGlassIcon className="w-5 h-5 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2 group-focus-within:text-brand-500 transition-colors" />
              <input 
                type="text" 
                placeholder="Search bulk jobs or records..." 
                className="w-full pl-12 pr-4 py-3 bg-slate-50 hover:bg-slate-100/80 border-none rounded-2xl text-[15px] font-medium text-slate-900 placeholder:text-slate-400 placeholder:font-normal focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:bg-white transition-all" 
              />
            </div>
          </div>
          
          {/* Right Header Actions */}
          <div className="flex items-center gap-6 ml-8">
            
            <div className="flex items-center gap-4 text-slate-500">
              <button className="hover:text-slate-900 hover:bg-slate-50 p-2 rounded-full transition-colors">
                <BellIcon className="w-6 h-6" />
              </button>
              <button className="hover:text-slate-900 hover:bg-slate-50 p-2 rounded-full transition-colors">
                <ClockIcon className="w-6 h-6" />
              </button>
            </div>

            <div className="h-8 w-px bg-slate-200" aria-hidden="true" />

            {/* Profile */}
            <div className="flex items-center gap-3 cursor-pointer group">
              <span className="font-semibold text-slate-700 text-[15px] group-hover:text-slate-900 transition-colors">
                Karthik Varma
              </span>
              <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center border-2 border-white shadow-sm group-hover:shadow-md transition-shadow">
                <span className="text-orange-600 font-bold text-sm tracking-wide">KV</span>
                {/* Status dot */}
                <div className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-emerald-500 border-2 border-white rounded-full"></div>
              </div>
            </div>

          </div>
        </header>

        {/* Dynamic Page Content */}
        <main className="flex-1 overflow-y-auto bg-surface-50">
          <Outlet />
        </main>
      </div>
    </div>
  );
}